import { AnthropicLogo, GeminiLogo, OpenAILogo } from '@/components/Landing/ProviderLogos'

type VendorIconProps = {
  name?: string
  icon?: string
  size?: number
  className?: string
}

type BrandMeta = {
  label: string
  bg: string
  fg: string
}

const brandMap: Record<string, BrandMeta> = {
  qwen: { label: 'QW', bg: '#EEF2FF', fg: '#2563EB' },
  alibaba: { label: 'QW', bg: '#EEF2FF', fg: '#2563EB' },
  deepseek: { label: 'DS', bg: '#EEF2FF', fg: '#4F46E5' },
  moonshot: { label: 'KM', bg: '#F5F3FF', fg: '#7C3AED' },
  kimi: { label: 'KM', bg: '#F5F3FF', fg: '#7C3AED' },
  zhipu: { label: 'GLM', bg: '#ECFEFF', fg: '#0891B2' },
  glm: { label: 'GLM', bg: '#ECFEFF', fg: '#0891B2' },
  minimax: { label: 'MM', bg: '#F0FDFA', fg: '#0F766E' },
  wenxin: { label: 'BD', bg: '#EFF6FF', fg: '#1D4ED8' },
  baidu: { label: 'BD', bg: '#EFF6FF', fg: '#1D4ED8' },
  ernie: { label: 'BD', bg: '#EFF6FF', fg: '#1D4ED8' },
  spark: { label: 'IF', bg: '#FFF7ED', fg: '#EA580C' },
  xunfei: { label: 'IF', bg: '#FFF7ED', fg: '#EA580C' },
  hunyuan: { label: 'HY', bg: '#F0F9FF', fg: '#0284C7' },
  tencent: { label: 'HY', bg: '#F0F9FF', fg: '#0284C7' },
  cohere: { label: 'CO', bg: '#F7FEE7', fg: '#4D7C0F' },
  cloudflare: { label: 'CF', bg: '#FFF7ED', fg: '#C2410C' },
  ai360: { label: '360', bg: '#F0FDF4', fg: '#16A34A' },
  '360': { label: '360', bg: '#F0FDF4', fg: '#16A34A' },
  yi: { label: '01', bg: '#F8FAFC', fg: '#334155' },
  jina: { label: 'JN', bg: '#FFF1F2', fg: '#E11D48' },
  mistral: { label: 'MI', bg: '#FEF3C7', fg: '#B45309' },
  xai: { label: 'xAI', bg: '#F8FAFC', fg: '#0F172A' },
  grok: { label: 'xAI', bg: '#F8FAFC', fg: '#0F172A' },
  ollama: { label: 'ME', bg: '#F1F5F9', fg: '#334155' },
  meta: { label: 'ME', bg: '#F1F5F9', fg: '#334155' },
  llama: { label: 'ME', bg: '#F1F5F9', fg: '#334155' },
  doubao: { label: 'DB', bg: '#F5F3FF', fg: '#6D28D9' },
  bytedance: { label: 'DB', bg: '#F5F3FF', fg: '#6D28D9' },
  kling: { label: 'KL', bg: '#ECFDF5', fg: '#047857' },
  jimeng: { label: 'JM', bg: '#FDF2F8', fg: '#DB2777' },
  vidu: { label: 'VD', bg: '#F0FDFA', fg: '#0D9488' },
  azureai: { label: 'AZ', bg: '#EFF6FF', fg: '#2563EB' },
  azure: { label: 'AZ', bg: '#EFF6FF', fg: '#2563EB' },
  microsoft: { label: 'AZ', bg: '#EFF6FF', fg: '#2563EB' },
}

function isImageIcon(value?: string) {
  if (!value) return false
  return /^(https?:)?\/\//i.test(value) || value.startsWith('/') || value.startsWith('data:image/') || value.startsWith('blob:')
}

function normalizeIconKey(value?: string) {
  return (value || '')
    .toLowerCase()
    .replace(/\.color$/i, '')
    .replace(/[^a-z0-9]/g, '')
}

function badgeText(name?: string, icon?: string) {
  const source = name || icon || 'AI'
  const ascii = source.match(/[A-Za-z0-9]+/g)?.join('')
  if (ascii) return ascii.slice(0, 3).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export function VendorIcon({ name, icon, size = 36, className }: VendorIconProps) {
  const key = normalizeIconKey(icon) || normalizeIconKey(name)
  const title = icon || name || 'AI'
  const innerSize = Math.max(16, Math.round(size * 0.58))

  if (isImageIcon(icon)) {
    return (
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center rounded-[10px] bg-white ring-1 ring-[#E2E8F0] ${className || ''}`}
        style={{ width: size, height: size }}
        title={title}
      >
        <img src={icon} alt="" className="rounded object-contain" style={{ width: innerSize, height: innerSize }} />
      </span>
    )
  }

  if (key === 'openai') {
    return (
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center rounded-[10px] bg-[#F8FAFC] text-[#111827] ring-1 ring-[#E2E8F0] ${className || ''}`}
        style={{ width: size, height: size }}
        title={title}
      >
        <OpenAILogo size={innerSize} />
      </span>
    )
  }

  if (key === 'claude' || key === 'anthropic') {
    return (
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center rounded-[10px] bg-[#FBF7EF] text-[#C15F3C] ring-1 ring-[#F1E4D4] ${className || ''}`}
        style={{ width: size, height: size }}
        title={title}
      >
        <AnthropicLogo size={innerSize} />
      </span>
    )
  }

  if (key === 'gemini' || key === 'google') {
    return (
      <span
        className={`inline-flex flex-shrink-0 items-center justify-center rounded-[10px] bg-[#F8FAFF] ring-1 ring-[#DBEAFE] ${className || ''}`}
        style={{ width: size, height: size }}
        title={title}
      >
        <GeminiLogo size={innerSize} />
      </span>
    )
  }

  const brand = brandMap[key]
  const label = brand?.label || badgeText(name, icon)
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center justify-center rounded-[10px] text-[11px] font-bold ring-1 ring-black/5 ${className || ''}`}
      style={{
        width: size,
        height: size,
        backgroundColor: brand?.bg || '#EEF2FF',
        color: brand?.fg || '#4F46E5',
      }}
      title={title}
    >
      {label}
    </span>
  )
}
